import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * The frame every application form sits in.
 *
 * The forms used to be a bare heading with an embedded form under it, on the page
 * background, while everything leading up to them - home page tiles, finder options -
 * was a card. So the journey went from something designed to something that looked
 * like a different site, at exactly the point the applicant is deciding whether to
 * trust it with their details.
 *
 * It also carries the promise forward. The finder tells someone their permit is
 * issued straight away, or that it goes to TLC for review; that was the reason they
 * were happy to continue, and it used to disappear the moment the form loaded.
 */
@Component({
    selector: 'app-apply-shell',
    templateUrl: './apply-shell.component.html',
    styleUrls: ['./apply-shell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ApplyShellComponent {
    /** e.g. "Tourist Pass" - the shell writes the "Apply for a" part. */
    @Input() permitLabel = '';

    /** What happens after they submit, in their words. Restates the finder's outcome. */
    @Input() outcome = '';

    /** Hidden once the form has been submitted - the result stands on its own. */
    @Input() showForm = true;

    /**
     * The finder step this form was reached from, e.g. `tourist-pass`.
     *
     * A route rather than browser history: someone who opened this form from a
     * bookmark, an email, or a link a colleague sent has no history to go back
     * through, and `location.back()` would either do nothing or throw them out of the
     * site entirely. Naming the step means the trail is the same however they arrived.
     */
    @Input() previousStep?: string;

    /**
     * The info page this form was reached from, e.g. `/tourist-permit` or `/recreation-area`.
     * Preferred over previousStep now that the entry flow runs through the info pages; a
     * route rather than history, for the same reason as previousStep.
     */
    @Input() previousRoute?: string;

    /** What that step is called, for the crumb. */
    @Input() previousLabel?: string;

    /**
     * Where the done state goes back to.
     *
     * Defaults to the permit list, because that is where everything using this shell
     * went until the Business Login application joined it - and someone who has just
     * applied for a login has no permits to be sent to.
     */
    @Input() doneLink = '/my-permits';
    @Input() doneLabel = 'All my permits';
}
